import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Trash2, FileText, Layers, Users, AlertCircle } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select } from '@/components/ui/Select'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Toggle } from '@/components/ui/Toggle'
import { NumberInput } from '@/components/ui/NumberInput'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { toast } from '@/components/ui/Toast'
import { ProductsService, type Product, type ProductWritePayload } from '@/services/modules/ProductsService'
import { ServicesService, type Service } from '@/services/modules/ServicesService'
import type { ServiceProvider } from '@/services/modules/ServiceProvidersService'
import { LookupService, type DegreeRelation } from '@/services/modules/LookupService'
import { api } from '@/services/api/AxiosBaseService'
import type { PaginatedResponse } from '@/types/api'
import { useAuthority } from '@/hooks/useAuthority'

/**
 * Cadastro/edição de Produto. 4 seções:
 *   1. Dados — nome, descrição, status
 *   2. Composição — linhas (Serviço × Prestador). REGRA: prestador sempre
 *      escolhido explicitamente, mesmo quando o serviço só tem 1 habilitado.
 *   3. Modalidades — quais modalidades o produto disponibiliza (toggle)
 *   4. Regras de elegibilidade — por modalidade, tabela de
 *      (Grau, Idade min, Idade max, Apólices adicionais)
 *
 * Sincronização: o backend faz REPLACE completo de services/modalities, então
 * o estado local representa fielmente o que será salvo.
 */

interface Modality {
  id: string
  name: string
  minAge: number | null
}

interface ServiceRow {
  serviceId: string
  serviceProviderId: string
}

interface DegreeRow {
  degreeRelationshipId: string
  minAge: number | null
  maxAge: number | null
  // Mantido como string para permitir vazio. Convertido em number ao enviar.
  additionalPolicies: string
}

interface ModalityRow {
  planTypeRelationshipId: string
  degrees: DegreeRow[]
}

export function ProductFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const nav = useNavigate()
  // POST /commercial/products → create; PUT /commercial/products/{id} → edit.
  const { can } = useAuthority()
  const canSave = can(isEdit ? 'commercial.products.edit' : 'commercial.products.create')

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [activeSection, setActiveSection] = useState('data')

  // dados
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  // composição
  const [services, setServices] = useState<ServiceRow[]>([])
  // modalidades
  const [modalities, setModalities] = useState<ModalityRow[]>([])

  // catálogos
  const [allServices, setAllServices] = useState<Service[]>([])
  const [allModalities, setAllModalities] = useState<Modality[]>([])
  const [allDegrees, setAllDegrees] = useState<DegreeRelation[]>([])

  // Validação só aparece DEPOIS de tentar salvar (igual aos demais forms).
  const [submitted, setSubmitted] = useState(false)

  // Carrega catálogos
  useEffect(() => {
    Promise.all([
      ServicesService.list({ size: 0, status: 'ATIVO' }).then(r => r.data),
      api.get<PaginatedResponse<Modality>>('/commercial/modalities', { params: { size: 0, status: 'ATIVO' } })
        .then(r => r.data.data),
      LookupService.degreeRelationships(100),
    ]).then(([svcs, mods, degs]) => {
      setAllServices(svcs)
      setAllModalities(mods)
      setAllDegrees(degs)
    })
  }, [])

  // Carrega produto em edição
  useEffect(() => {
    if (!id) return
    setLoading(true)
    ProductsService.show(id)
      .then((p: Product) => {
        setName(p.name)
        setDescription(p.description ?? '')
        setStatus(p.status)
        setServices((p.services ?? []).map(s => ({
          serviceId: s.serviceId,
          serviceProviderId: s.serviceProviderId,
        })))
        setModalities((p.modalities ?? []).map(m => ({
          planTypeRelationshipId: m.planTypeRelationshipId,
          degrees: (m.degrees ?? []).map(d => ({
            degreeRelationshipId: d.degreeRelationshipId,
            minAge: d.minAge,
            maxAge: d.maxAge,
            additionalPolicies: String(d.additionalPolicies ?? ''),
          })),
        })))
      })
      .catch(() => toast.error({ title: 'Erro ao carregar', description: 'Produto não encontrado.' }))
      .finally(() => setLoading(false))
  }, [id])

  // Lookup: para um service_id, quais prestadores estão habilitados.
  const providersForService = useMemo(() => {
    const map = new Map<string, ServiceProvider[]>()
    for (const s of allServices) {
      map.set(s.id, s.providers ?? [])
    }
    return map
  }, [allServices])

  function addService() {
    setServices(prev => [...prev, { serviceId: '', serviceProviderId: '' }])
  }
  function updateService(idx: number, patch: Partial<ServiceRow>) {
    setServices(prev => prev.map((row, i) => {
      if (i !== idx) return row
      const next = { ...row, ...patch }
      // se mudou o serviço, resetar o prestador (pode não estar habilitado)
      if (patch.serviceId !== undefined && patch.serviceId !== row.serviceId) {
        next.serviceProviderId = ''
      }
      return next
    }))
  }
  function removeService(idx: number) {
    setServices(prev => prev.filter((_, i) => i !== idx))
  }

  function toggleModality(modId: string) {
    setModalities(prev => {
      const idx = prev.findIndex(m => m.planTypeRelationshipId === modId)
      if (idx === -1) return [...prev, { planTypeRelationshipId: modId, degrees: [] }]
      return prev.filter(m => m.planTypeRelationshipId !== modId)
    })
  }

  function addDegree(modIdx: number) {
    setModalities(prev => prev.map((m, i) => i === modIdx
      ? { ...m, degrees: [...m.degrees, { degreeRelationshipId: '', minAge: null, maxAge: null, additionalPolicies: '' }] }
      : m))
  }
  function updateDegree(modIdx: number, dIdx: number, patch: Partial<DegreeRow>) {
    setModalities(prev => prev.map((m, i) => i === modIdx
      ? { ...m, degrees: m.degrees.map((d, j) => j === dIdx ? { ...d, ...patch } : d) }
      : m))
  }
  function removeDegree(modIdx: number, dIdx: number) {
    setModalities(prev => prev.map((m, i) => i === modIdx
      ? { ...m, degrees: m.degrees.filter((_, j) => j !== dIdx) }
      : m))
  }

  /**
   * Erros granulares por campo. Recomputa a cada render para refletir
   * imediatamente o estado do form — mas só são EXIBIDOS depois que o
   * usuário tentou salvar (`submitted`). Padrão do sistema.
   */
  const validation = useMemo(() => {
    // Estrutura espelha o form para fácil acesso na render.
    const data: { name?: string } = {}
    const servicesErr: Array<{ service?: string; provider?: string }> = services.map(() => ({}))
    const modalitiesErr: { atLeastOne?: string } = {}
    const rulesErr: Array<Array<{ degree?: string; age?: string }>> = modalities.map(m => m.degrees.map(() => ({})))

    if (!name.trim()) data.name = 'Informe o nome do produto.'

    // REGRA: produto precisa de pelo menos 1 serviço
    const servicesEmpty = services.length === 0 ? 1 : 0

    services.forEach((s, i) => {
      if (!s.serviceId) servicesErr[i].service = 'Selecione o serviço.'
      if (!s.serviceProviderId) servicesErr[i].provider = 'Selecione o prestador.'
    })

    if (modalities.length === 0) {
      modalitiesErr.atLeastOne = 'Selecione pelo menos uma modalidade.'
    }

    modalities.forEach((m, mi) => {
      m.degrees.forEach((d, di) => {
        if (!d.degreeRelationshipId) {
          rulesErr[mi][di].degree = 'Selecione o grau.'
        }
        if (d.minAge !== null && d.maxAge !== null && d.minAge > d.maxAge) {
          rulesErr[mi][di].age = 'Idade mínima maior que máxima.'
        }
      })
    })

    const dataCount = data.name ? 1 : 0
    const servicesCount = servicesEmpty + servicesErr.reduce((acc, e) => acc + (e.service ? 1 : 0) + (e.provider ? 1 : 0), 0)
    const modalitiesCount = modalitiesErr.atLeastOne ? 1 : 0
    const rulesCount = rulesErr.reduce(
      (acc, arr) => acc + arr.reduce((a, e) => a + (e.degree ? 1 : 0) + (e.age ? 1 : 0), 0),
      0,
    )
    const total = dataCount + servicesCount + modalitiesCount + rulesCount

    const servicesAtLeastOne = servicesEmpty ? 'Adicione pelo menos um serviço.' : undefined

    return {
      data,
      servicesErr,
      servicesAtLeastOne,
      modalitiesErr,
      rulesErr,
      counts: { data: dataCount, services: servicesCount, modalities: modalitiesCount, rules: rulesCount, total },
    }
  }, [name, services, modalities])

  // Erros só "aparecem" depois de tentar salvar; antes, tudo vazio.
  const errs: typeof validation = submitted ? validation : {
    data: {},
    servicesErr: services.map(() => ({} as { service?: string; provider?: string })),
    servicesAtLeastOne: undefined,
    modalitiesErr: {},
    rulesErr: modalities.map(m => m.degrees.map(() => ({} as { degree?: string; age?: string }))),
    counts: { data: 0, services: 0, modalities: 0, rules: 0, total: 0 },
  }

  function attemptSubmit() {
    setSubmitted(true)
    if (validation.counts.total === 0) {
      void submit()
      return
    }
    toast.error({ title: 'Verifique os campos', description: 'Há campos obrigatórios pendentes.' })
    const order: Array<'data' | 'services' | 'modalities' | 'rules'> = ['data', 'services', 'modalities', 'rules']
    const firstErr = order.find(k => validation.counts[k] > 0)
    if (firstErr) setActiveSection(firstErr)
  }

  async function submit() {
    const payload: ProductWritePayload = {
      name: name.trim(),
      description: description.trim() || null,
      status,
      services: services.map(s => ({
        service_id: s.serviceId,
        service_provider_id: s.serviceProviderId,
      })),
      modalities: modalities.map(m => ({
        plan_type_relationship_id: m.planTypeRelationshipId,
        degrees: m.degrees.map(d => ({
          degree_relationship_id: d.degreeRelationshipId,
          min_age: d.minAge,
          max_age: d.maxAge,
          // string vazia → 0; string numérica → number
          additional_policies: d.additionalPolicies === '' ? 0 : Number(d.additionalPolicies),
        })),
      })),
    }
    setSaving(true)
    try {
      if (isEdit && id) {
        await ProductsService.update(id, payload)
        toast.success({ title: 'Produto atualizado', description: name })
      } else {
        await ProductsService.create(payload)
        toast.success({ title: 'Produto criado', description: name })
      }
      nav('/settings/commercial/products')
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.'
      toast.error({ title: 'Não foi possível salvar', description: msg })
    } finally {
      setSaving(false)
    }
  }

  // ===================== Sections =====================

  const dataSection = (
    <SectionShell
      title="Dados"
      description="Informações básicas do produto."
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Input
          label="Nome"
          required
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Plano Assistencial + Tech + SF Porto"
          wrapperClassName="md:col-span-2"
          error={errs.data.name}
        />
        <div className="md:col-span-1">
          <StatusToggle value={status} onChange={setStatus} />
        </div>
        <Textarea
          label="Descrição"
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={3}
          wrapperClassName="md:col-span-3"
          placeholder="Observações internas sobre o produto"
        />
      </div>
    </SectionShell>
  )

  const composicaoSection = (
    <SectionShell
      title="Composição"
      description="Cada linha é um serviço incluso no produto. O prestador é sempre escolhido explicitamente — mesmo quando o serviço tem apenas 1 prestador habilitado."
    >
      {errs.servicesAtLeastOne && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {errs.servicesAtLeastOne}
        </div>
      )}
      <div className="space-y-3">
        {services.length === 0 && (
          <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            Nenhum serviço incluído. Clique em <strong>Adicionar serviço</strong>.
          </div>
        )}

        {services.map((row, idx) => {
          const provOpts = providersForService.get(row.serviceId) ?? []
          // Cada serviço só pode aparecer 1 vez no produto — filtra os já escolhidos
          // em OUTRAS linhas. A própria linha mantém seu valor selecionável.
          const usedServiceIds = new Set(
            services.filter((_, i) => i !== idx).map(s => s.serviceId).filter(Boolean),
          )
          const serviceOpts = allServices
            .filter(s => !usedServiceIds.has(s.id))
            .map(s => ({ value: s.id, label: s.name }))

          return (
            <div key={idx} className="grid grid-cols-1 items-end gap-3 rounded-md border border-gray-200 p-3 md:grid-cols-[1fr_1fr_auto]">
              <Select
                label="Serviço"
                required
                value={row.serviceId}
                onChange={e => updateService(idx, { serviceId: e.target.value })}
                options={serviceOpts}
                placeholder="Selecione o serviço"
                error={errs.servicesErr[idx]?.service}
              />
              <Select
                label="Prestador"
                required
                value={row.serviceProviderId}
                onChange={e => updateService(idx, { serviceProviderId: e.target.value })}
                options={provOpts.map(p => ({ value: p.id, label: p.name }))}
                placeholder={row.serviceId ? 'Selecione o prestador' : 'Escolha o serviço primeiro'}
                disabled={!row.serviceId}
                error={errs.servicesErr[idx]?.provider}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeService(idx)}
                iconLeft={<Trash2 size={14} />}
                className="text-red-600 hover:bg-red-50"
              >
                Remover
              </Button>
            </div>
          )
        })}

        {/* Botão Add desabilitado quando todos os serviços já foram escolhidos */}
        <Button
          variant="ghost"
          onClick={addService}
          iconLeft={<Plus size={14} />}
          disabled={services.length >= allServices.length}
        >
          Adicionar serviço
        </Button>
        {services.length >= allServices.length && allServices.length > 0 && (
          <p className="text-xs text-gray-400">Todos os serviços cadastrados já foram incluídos.</p>
        )}
      </div>
    </SectionShell>
  )

  const modalitiesSection = (
    <SectionShell
      title="Modalidades"
      description="Marque as modalidades em que este produto poderá ser comercializado. As regras de cada modalidade ficam na próxima aba."
    >
      {errs.modalitiesErr.atLeastOne && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {errs.modalitiesErr.atLeastOne}
        </div>
      )}
      {allModalities.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Nenhuma modalidade cadastrada. Cadastre em <strong>Configurações → Comercial → Modalidades</strong>.
        </div>
      ) : (
        <div className="rounded-md border border-gray-200">
          <div className="border-b border-gray-200 px-3 py-2 text-xs text-gray-500">
            {modalities.length} de {allModalities.length} selecionada{modalities.length === 1 ? '' : 's'}
          </div>
          <ul className="divide-y divide-gray-100">
            {allModalities.map(mod => {
              const checked = modalities.some(m => m.planTypeRelationshipId === mod.id)
              return (
                <li key={mod.id} className="flex items-center justify-between px-3 py-2">
                  <div className="text-sm text-gray-700">
                    {mod.name}
                    {mod.minAge != null && (
                      <span className="ml-2 text-xs text-gray-400">(a partir de {mod.minAge} anos)</span>
                    )}
                  </div>
                  <Toggle
                    checked={checked}
                    onChange={() => toggleModality(mod.id)}
                  />
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </SectionShell>
  )

  const rulesSection = (
    <SectionShell
      title="Regras de elegibilidade"
      description="Para cada modalidade selecionada, defina os graus de parentesco aceitos, faixa etária e apólices adicionais."
    >
      <div className="space-y-6">
        {modalities.length === 0 && (
          <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            Nenhuma modalidade selecionada. Vá em <strong>Modalidades</strong> e marque pelo menos uma.
          </div>
        )}

        {modalities.map((m, modIdx) => {
          const mod = allModalities.find(x => x.id === m.planTypeRelationshipId)
          // Cada grau só pode aparecer 1x dentro da modalidade
          const usedDegreeIdsInThisModality = new Set(
            m.degrees.map(d => d.degreeRelationshipId).filter(Boolean),
          )
          const allDegreesUsed = usedDegreeIdsInThisModality.size >= allDegrees.length

          return (
            <div key={m.planTypeRelationshipId} className="rounded-md border border-gray-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-800">{mod?.name ?? '—'}</h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => addDegree(modIdx)}
                  iconLeft={<Plus size={14} />}
                  disabled={allDegreesUsed}
                >
                  Adicionar grau
                </Button>
              </div>

              {m.degrees.length === 0 ? (
                <p className="text-xs text-gray-400">Nenhum grau definido — adicione ao menos o titular.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                        <th className="pb-2 font-medium">Grau de parentesco</th>
                        <th className="pb-2 font-medium">Idade mín.</th>
                        <th className="pb-2 font-medium">Idade máx.</th>
                        <th className="pb-2 font-medium">Apólices adicionais</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.degrees.map((d, dIdx) => {
                        // Cada grau só pode aparecer 1x — filtra os já escolhidos em outras linhas
                        const usedOther = new Set(
                          m.degrees.filter((_, j) => j !== dIdx).map(x => x.degreeRelationshipId).filter(Boolean),
                        )
                        const degreeOpts = allDegrees
                          .filter(g => !usedOther.has(g.id))
                          .map(g => ({ value: g.id, label: g.degreeRelationship }))

                        const rowErr = errs.rulesErr[modIdx]?.[dIdx] ?? {}
                        return (
                          <tr key={dIdx} className="border-t border-gray-100">
                            <td className="py-2 pr-2 align-top min-w-[200px]">
                              <Select
                                value={d.degreeRelationshipId}
                                onChange={e => updateDegree(modIdx, dIdx, { degreeRelationshipId: e.target.value })}
                                options={degreeOpts}
                                placeholder="Selecione o grau"
                                error={rowErr.degree}
                              />
                            </td>
                            <td className="py-2 pr-2 align-top w-32">
                              <NumberInput
                                value={d.minAge}
                                onChange={v => updateDegree(modIdx, dIdx, { minAge: v ?? null })}
                                min={0}
                                max={120}
                                placeholder="—"
                                error={rowErr.age}
                              />
                            </td>
                            <td className="py-2 pr-2 align-top w-32">
                              <NumberInput
                                value={d.maxAge}
                                onChange={v => updateDegree(modIdx, dIdx, { maxAge: v ?? null })}
                                min={0}
                                max={120}
                                placeholder="—"
                              />
                            </td>
                            <td className="py-2 pr-2 align-top w-36">
                              {/* Plain Input — vazio por default, aceita só dígitos */}
                              <Input
                                value={d.additionalPolicies}
                                onChange={e => updateDegree(modIdx, dIdx, {
                                  additionalPolicies: e.target.value.replace(/\D+/g, '').slice(0, 2),
                                })}
                                placeholder="—"
                                inputMode="numeric"
                                clearable={false}
                              />
                            </td>
                            <td className="py-2 align-top w-12 text-right">
                              <button
                                type="button"
                                onClick={() => removeDegree(modIdx, dIdx)}
                                className="cursor-pointer rounded-md p-1.5 text-red-600 hover:bg-red-50"
                                title="Remover grau"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </SectionShell>
  )

  const sections: FormSection[] = [
    { key: 'data',       label: 'Dados',                icon: FileText,    content: dataSection,         hasError: errs.counts.data > 0,       errorCount: errs.counts.data },
    { key: 'services',   label: 'Composição',           icon: Layers,      content: composicaoSection,   hasError: errs.counts.services > 0,   errorCount: errs.counts.services },
    { key: 'modalities', label: 'Modalidades',          icon: Users,       content: modalitiesSection,   hasError: errs.counts.modalities > 0, errorCount: errs.counts.modalities },
    { key: 'rules',      label: 'Regras de elegibilidade', icon: AlertCircle, content: rulesSection,     hasError: errs.counts.rules > 0,      errorCount: errs.counts.rules },
  ]

  return (
    <>
      <PageHeader
        title={isEdit ? 'Editar produto' : 'Novo produto'}
        subtitle={isEdit ? name : 'Configure o bundle de serviços e as regras de elegibilidade'}
        backTo="/settings/commercial/products"
        crumbs={[
          { label: 'Configurações', to: '/settings' },
          { label: 'Comercial' },
          { label: 'Produtos', to: '/settings/commercial/products' },
          { label: isEdit ? 'Editar' : 'Novo' },
        ]}
        actions={
          <Button onClick={attemptSubmit} loading={saving} disabled={loading || !canSave}
            title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} produto`}>
            {isEdit ? 'Atualizar' : 'Criar produto'}
          </Button>
        }
      />
      {loading ? (
        <div className="rounded-md border border-gray-200 p-6 text-sm text-gray-500">Carregando</div>
      ) : (
        <FormSidemenu
          sections={sections}
          activeKey={activeSection}
          onChange={setActiveSection}
        />
      )}
    </>
  )
}

/**
 * Wrapper das seções do form — fundo branco + ring, padrão global usado nos
 * outros forms (Plans, Companies, etc.).
 */
function SectionShell({ title, description, children }: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}
